import {announce} from '@react-aria/live-announcer'
import {useNavigate} from '@tanstack/react-router'
import {toast} from 'sonner'

import SkipLink from '@/components/SkipLink'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import HeadTags from '@/lib/head/HeadTags'
import useStore from '@/lib/store/useStore'
import type {StoreApiResponse} from '@/models/storeModel'
import {StoreRoute} from '@/routeRegistry'
import useSearch from '@/utils/router/useSearch'

import StoreHero from './components/StoreHero'
import StoreResultsSection from './components/StoreResultsSection'
import StoreSection from './components/StoreSection'
import StoreUtilityRow from './components/StoreUtilityRow'
import style from './StoreFront.module.scss'

export default function StoreFront() {
  const {storeId} = StoreRoute.useParams()
  const {data}: {data: StoreApiResponse | undefined} = useStore(storeId)
  const title = data?.display.name ?? storeId
  const bannerSrc = data?.display.banner
  const bannerMobileSrc = data?.display['banner-mobile']
  const navigate = useNavigate()
  const sectionsWithProducts = useMemo(() => {
    const sections = data?.sections ?? []
    return sections.filter(section => section.products.length > 0)
  }, [data?.sections])

  const allProducts = useMemo(
    () => sectionsWithProducts.flatMap(s => s.products),
    [sectionsWithProducts],
  )

  const [q, setQ] = useSearch(StoreRoute.id, 'q', {
    replace: false,
    resetScroll: true,
  })

  const hasSearchQuery = Boolean(q)

  const resultProducts = useMemo(() => {
    if (!hasSearchQuery) return []
    return allProducts.filter(p => `${p.title} ${p.retailer}`.toLowerCase().includes(q))
  }, [allProducts, q, hasSearchQuery])

  useEffect(
    function announceSearchResults() {
      const total = resultProducts.length
      const announcedTerm = q
      if (hasSearchQuery && announcedTerm) {
        announce(`${total.toString()} results for ${announcedTerm}`, 'assertive')
      } else if (!hasSearchQuery) {
        announce('Showing all products', 'assertive')
      }
    },
    [hasSearchQuery, q, resultProducts.length],
  )

  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const toggleExpanded = useCallback((title: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(title)) next.delete(title)
      else next.add(title)
      return next
    })
  }, [])

  const handleShare = async () => {
    const url = window.location.href
    const docTitle = document.title

    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({title: docTitle, url})
      } else if (typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(url)
        toast.success('Link copied to clipboard')
        announce('Link copied to clipboard', 'polite')
      }
    } catch (error) {
      const err = error as {name?: string}
      if (err.name === 'AbortError' || err.name === 'NotAllowedError') return

      toast.error('Unable to share link. Please copy it manually.')
      announce('Unable to share link. Please copy it manually.', 'polite')
    }
  }

  return (
    <div className={style.Page}>
      <HeadTags title={title} />
      <SkipLink title='Skip to main content' to='#main-content' />
      <Navbar variant='dark' withSearch />

      <div className={style.Content}>
        <StoreHero title={title} bannerSrc={bannerSrc} bannerMobileSrc={bannerMobileSrc} />
        <StoreUtilityRow
          hasActiveSearch={hasSearchQuery}
          defaultValue={q || ''}
          onSearch={searchTerm => {
            if (searchTerm) {
              void setQ(searchTerm)
            }
          }}
          onClear={() => {
            void navigate({
              to: '/stores/$storeId',
              params: {storeId},
              search: {},
            })
          }}
          onShare={handleShare}
        />

        <div
          className={
            hasSearchQuery && resultProducts.length === 0
              ? `${style.ProductsSection} ${style.NoBg}`
              : style.ProductsSection
          }
        >
          <main className={style.ProductsContainer}>
            {hasSearchQuery && (
              <StoreResultsSection title={title} q={q} products={resultProducts} />
            )}

            {!hasSearchQuery &&
              sectionsWithProducts.length > 0 &&
              sectionsWithProducts.map(section => {
                const expanded = expandedSections.has(section.title)
                return (
                  <StoreSection
                    key={section.title}
                    title={section.title}
                    products={section.products}
                    expanded={expanded}
                    onToggle={() => {
                      toggleExpanded(section.title)
                    }}
                  />
                )
              })}

            {!hasSearchQuery && sectionsWithProducts.length === 0 && (
              <div className={style.EmptyState}>
                <div className={style.EmptyStateContent}>
                  <h2 className={style.EmptyStateTitle}>Something exciting is coming</h2>
                  <p className={style.EmptyStateSubtitle}>
                    Amazing products from {title} are on their way. Stay tuned!
                  </p>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  )
}
